import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, TextField, Table, TableBody, TableCell, 
  TableHead, TableRow, Paper, IconButton, Chip, Dialog, DialogTitle, 
  DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Grid, InputAdornment, Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';

function Contracts() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const navigate = useNavigate();

  // Form Data
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [countries, setCountries] = useState([]);
  const [divisions, setDivisions] = useState([]);
  
  const [newContract, setNewContract] = useState({
      project_id: '',
      client_id: '',
      country_id: '',
      division_id: '',
      description: '',
      ddp: '',
      file: null
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [conRes, projRes, cliRes, couRes, divRes] = await Promise.all([
          axios.get(`${API_URL}/contracts`, { headers }),
          axios.get(`${API_URL}/projects`, { headers }),
          axios.get(`${API_URL}/clients`, { headers }),
          axios.get(`${API_URL}/setup/countries`, { headers }),
          axios.get(`${API_URL}/setup/divisions`, { headers })
      ]);
      setContracts(conRes.data);
      setProjects(projRes.data);
      setClients(cliRes.data);
      setCountries(couRes.data);
      setDivisions(divRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async () => {
      setCreateLoading(true);
      try {
          const formData = new FormData();
          formData.append('project_id', newContract.project_id);
          formData.append('client_id', newContract.client_id);
          formData.append('country_id', newContract.country_id);
          formData.append('division_id', newContract.division_id);
          formData.append('description', newContract.description);
          formData.append('ddp', newContract.ddp);
          if (newContract.file) {
              formData.append('contract_pdf', newContract.file);
          }

          await axios.post(`${API_URL}/contracts`, formData, {
              headers: { ...headers, 'Content-Type': 'multipart/form-data' }
          });
          setOpenAdd(false);
          fetchData();
          setNewContract({
            project_id: '', client_id: '', country_id: '', division_id: '', 
            description: '', ddp: '', file: null
          });
      } catch (err) {
          alert('Error creating contract');
          console.error(err);
      } finally {
          setCreateLoading(false);
      }
  };

  const getStatusColor = (status) => {
      switch(status) {
          case 'Signed': return 'success';
          case 'Done by Client': return 'info';
          case 'In Progress': return 'warning';
          default: return 'default';
      }
  };

  const filteredContracts = contracts.filter(c => {
      if (filter !== 'All' && c.status !== filter) return false;
      if (search) {
          const lowerSearch = search.toLowerCase();
          return (
              (c.client_name && c.client_name.toLowerCase().includes(lowerSearch)) ||
              (c.project_name && c.project_name.toLowerCase().includes(lowerSearch)) ||
              (c.description && c.description.toLowerCase().includes(lowerSearch))
          );
      }
      return true;
  });

  if (loading) return <Loading />;

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', md: 'center' }, mb: 3, gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {['All', 'New', 'In Progress', 'Done by Client', 'Signed'].map(f => (
                <Chip 
                    key={f} 
                    label={f} 
                    onClick={() => setFilter(f)} 
                    color={filter === f ? 'primary' : 'default'} 
                    variant={filter === f ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 'bold' }}
                />
            ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField 
                placeholder="Search contracts..." 
                size="small" 
                value={search} 
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                    sx: { borderRadius: 5, bgcolor: 'white' }
                }}
                sx={{ flexGrow: 1 }}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAdd(true)} sx={{ borderRadius: 5, whiteSpace: 'nowrap' }}>New Contract</Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Box sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 800 }}>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Client</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Project</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Created By</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {filteredContracts.map(c => (
                    <TableRow key={c.id} hover>
                        <TableCell>{c.id}</TableCell>
                        <TableCell>{c.client_name}</TableCell>
                        <TableCell>{c.project_name}</TableCell>
                        <TableCell sx={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {c.description}
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2">{c.country} - {c.division}</Typography>
                            <Typography variant="caption" color="textSecondary">{c.ddp}</Typography>
                        </TableCell>
                        <TableCell>
                            <Chip label={c.status} color={getStatusColor(c.status)} size="small" />
                        </TableCell>
                        <TableCell>{c.created_by}</TableCell>
                        <TableCell align="right">
                            <IconButton color="primary" onClick={() => navigate(`/contracts/${c.id}`)}>
                                <VisibilityIcon />
                            </IconButton>
                        </TableCell>
                    </TableRow>
                ))}
                {contracts.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            No contracts found.
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
        </Box>
      </Paper>

      {/* Add Dialog */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Contract</DialogTitle>
        <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Autocomplete
                            options={clients}
                            getOptionLabel={(option) => option.name || ""}
                            value={clients.find(c => c.id === newContract.client_id) || null}
                            onChange={(event, newValue) => {
                                setNewContract({...newContract, client_id: newValue ? newValue.id : ''});
                            }}
                            renderInput={(params) => <TextField {...params} label="Client" fullWidth />}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <Autocomplete
                            options={projects}
                            getOptionLabel={(option) => option.name || ""}
                            value={projects.find(p => p.id === newContract.project_id) || null}
                            onChange={(event, newValue) => {
                                setNewContract({...newContract, project_id: newValue ? newValue.id : ''});
                            }}
                            renderInput={(params) => <TextField {...params} label="Project (Optional)" fullWidth />}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel>Country</InputLabel>
                            <Select 
                                value={newContract.country_id} 
                                label="Country" 
                                onChange={e => setNewContract({...newContract, country_id: e.target.value})}
                            >
                                {countries.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel>Division</InputLabel>
                            <Select 
                                value={newContract.division_id} 
                                label="Division" 
                                onChange={e => setNewContract({...newContract, division_id: e.target.value})}
                            >
                                {divisions.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel>DDP Location</InputLabel>
                            <Select 
                                value={newContract.ddp} 
                                label="DDP Location" 
                                onChange={e => setNewContract({...newContract, ddp: e.target.value})}
                            >
                                <MenuItem value="Lebanon">Lebanon</MenuItem>
                                <MenuItem value="KSA">KSA</MenuItem>
                                <MenuItem value="Qatar">Qatar</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField 
                            fullWidth 
                            multiline 
                            rows={3} 
                            label="Description" 
                            value={newContract.description} 
                            onChange={e => setNewContract({...newContract, description: e.target.value})} 
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="outlined" component="label" fullWidth>
                            Upload Contract PDF
                            <input 
                                type="file" 
                                hidden 
                                accept="application/pdf" 
                                onChange={e => setNewContract({...newContract, file: e.target.files[0]})} 
                            />
                        </Button>
                        {newContract.file && <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>{newContract.file.name}</Typography>}
                    </Grid>
                </Grid>
            </Box>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
            <Button onClick={handleCreate} variant="contained" disabled={createLoading}>
              {createLoading ? 'Creating...' : 'Create'}
            </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Contracts;
